"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

type BottomToastProps = {
  isVisible: boolean;
  message: string;
  onClose: () => void;
  variant?: "error" | "default" | "copy" | "scrap" | "success";
  autoHideMs?: number;
};

export default function BottomToast({ isVisible, message, onClose, variant = "default", autoHideMs = 3500 }: BottomToastProps) {
  const [shouldRender, setShouldRender] = useState(isVisible);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
    } else {
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    const t = setTimeout(() => onClose(), autoHideMs);
    return () => clearTimeout(t);
  }, [isVisible, autoHideMs, onClose]);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!shouldRender || !mounted) return null;

  const toast = (
    <div
      className={`fixed bottom-[60px] left-1/2 transform -translate-x-1/2 z-[9999] transition-all duration-300 ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
      }`}
    >
      <div data-layer="얼럿_토스트 메시지" className="w-[90vw] sm:w-[600px] min-h-[76px] px-6 py-4 bg-white/70 rounded-[16px] shadow-[0px_0px_18px_0px_rgba(162,171,173,0.20)] inline-flex items-center justify-center backdrop-blur-[4px]">
        <div data-layer="Frame 2861" className="flex items-center gap-3 w-full min-h-[44px]">
          {variant === "error" && (
            <img src="/svg/error.svg" alt="error" width={24} height={24} className="flex-shrink-0" />
          )}
          {variant === "success" && (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
              <circle cx="12" cy="12" r="9" stroke="#22C55E" strokeWidth="1.4" />
              <path 
                d="M8.5 12L11 14.5L16 9.5" 
                stroke="#22C55E" 
                strokeWidth="1.4" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          )}
          {variant === "copy" && (
            <img src="/svg/clip.svg" alt="copy" width={24} height={24} className="flex-shrink-0" />
          )}
          {variant === "scrap" && (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
              <path 
                d="M18 6.7V18.3c0 .76-.81 1.24-1.48.88l-4.04-2.2a1 1 0 0 0-.96 0l-4.04 2.2A1 1 0 0 1 6 18.3V6.7A2.7 2.7 0 0 1 8.7 4h6.6A2.7 2.7 0 0 1 18 6.7Z" 
                stroke="#2563eb" 
                strokeWidth="1.4" 
                fill="#2563eb"
              />
            </svg>
          )}
          <div className={`text-[#454852] text-[16px] sm:text-[18px] font-normal leading-relaxed flex-1`} style={{ 
            fontFamily: 'var(--font-pretendard)',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {message}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(toast, document.body);
}


